//  src/waiter/OrdersBarSection.js
//
//  Dashboard camerieri – BAR
//  ----------------------------------------------------------
//  TAB “Da preparare”  → is_kitchen_order === false  + item pending / in_progress
//  TAB “Pronti”        → qualsiasi ordine con TUTTI gli item in “ready”
//
import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../services/api";

/* ---------- Palette + stili ---------- */
const palette = {
  primary : "#FF7F00",
  success : "#32CD32",
  danger  : "#EB5757",
  gray    : "#DDDDDD",
  card    : "#FFFFFF",
  text    : "#333",
};

const styles = {
  wrapper : { flex:1, display:"flex", flexDirection:"column" },

  tabsRow : { display:"flex", marginBottom:10 },
  tabBtn  : (active) => ({
    flex:1, padding:"10px 0", textAlign:"center",
    cursor:"pointer", fontWeight:"bold", userSelect:"none",
    color: active ? "#fff" : palette.text,
    background: active ? palette.primary : palette.gray,
  }),

  list: { flex:1, display:"flex", flexDirection:"column", gap:12, overflowY:"auto" },
  card: {
    background: palette.card, border:"1px solid #ddd", borderRadius:8,
    boxShadow:"0 1px 3px rgba(0,0,0,.08)", padding:10, fontSize:15,
  },

  cardHead: { display:"flex", justifyContent:"space-between", marginBottom:6 },
  tblLbl  : { fontWeight:"bold", color: palette.primary },
  tblNum  : { fontWeight:"bold", color: palette.primary },

  waiterRow:{ fontSize:14, fontStyle:"italic", marginBottom:6, color:"#666" },

  itemRow : { display:"flex", justifyContent:"space-between", margin:"4px 0" },

  btn:(bg)=>({
    background:bg, border:"none", color:"#fff", padding:"6px 10px",
    borderRadius:4, cursor:"pointer", fontSize:13, fontWeight:"bold",
  }),

  empty:{ textAlign:"center", color:"#888", marginTop:20, fontStyle:"italic" },
};

/* =============================================================== */
export default function OrdersBarSection({
  orders               = [],           // array dal genitore
  myWaiterName         = null,         // se definito ⇒ filtro “solo i miei”
  onReadyCountChange   = () => {},     // badge nella bottom‑nav
  refreshOrders        = () => {},     // refetch dal genitore
}) {
  /* ---------- stato locale (optimistic) ---------- */
  const [localOrders, setLocalOrders] = useState(orders);
  useEffect(() => setLocalOrders(orders), [orders]);

  /* ---------- filtri ---------- */
  const filteredOrders = useMemo(() => {
    if (!myWaiterName) return localOrders;
    return localOrders.filter(o => o.waiter_username === myWaiterName);
  }, [localOrders, myWaiterName]);

  const listTodo = useMemo(() =>
    filteredOrders.filter(o =>
      o.is_kitchen_order === false &&
      o.items?.some(it => ["pending","in_progress"].includes(it.status))
    ), [filteredOrders]);

  const listReady = useMemo(() =>
    filteredOrders.filter(o =>
      o.items?.length && o.items.every(it => it.status === "ready")
    ), [filteredOrders]);

  /* ---------- badge “pronti” ---------- */
  const readyCntRef = useRef(-1);
  useEffect(() => {
    if (readyCntRef.current !== listReady.length) {
      readyCntRef.current = listReady.length;
      onReadyCountChange(listReady.length);
    }
  }, [listReady.length, onReadyCountChange]);

  /* ===============================================================
     funzioni helper  (immutability semplificata)
  =============================================================== */
  /** aggiorna un item in localOrders (immutabile) e ritorna il nuovo array */
  const mutateOrders = (prevOrders, itemId, updater) =>
    prevOrders.map(o => ({
      ...o,
      items: o.items.map(it => it.id === itemId ? updater(it) : it)
    }));

  /** forceRefresh – sincronizza lo stato locale con il backend */
  const forceRefresh = async () => {
    try { await refreshOrders(); }
    catch (e) { console.error("forceRefresh", e); }
  };

  /* ===============================================================
     actions
  =============================================================== */
  /** PATCH stato item + optimistic update */
  const changeItemStatus = async (itemId, nextStatus) => {
    // optimistic
    setLocalOrders(prev => mutateOrders(prev, itemId, it => ({ ...it, status: nextStatus })));

    try {
      await api.patch(`/kitchen/order_items/${itemId}/status`, { status: nextStatus });
    } catch (err) {
      console.error("changeItemStatus:", err);
    } finally {
      forceRefresh();                // riallinea dal server
    }
  };

  /** consegna ordine ⇒ tutti gli item ready diventano delivered  */
  const deliverOrder = async (ord) => {
    // optimistic
    setLocalOrders(prev =>
      prev.map(o => o.id === ord.id
        ? {
            ...o,
            items: o.items.map(it =>
              it.status === "ready" ? { ...it, status:"delivered" } : it)
          }
        : o
      )
    );

    // PATCH in parallelo per ogni item ready
    try {
      const readyItems = ord.items.filter(it => it.status === "ready");
      await Promise.all(
        readyItems.map(it =>
          api.patch(`/orders/${ord.id}/items/${it.id}/deliver`)
        )
      );
    } catch (err) {
      console.error("deliverOrder:", err);
    } finally {
      forceRefresh();
    }
  };

  /* ===============================================================
     rendering helpers
  =============================================================== */
  const renderCard = (ord, mode) => (
    <div key={ord.id} style={styles.card}>
      <div style={styles.cardHead}>
        <span><strong>Ordine&nbsp;#{ord.id}</strong></span>
        <span>
          <span style={styles.tblLbl}>Tavolo&nbsp;</span>
          <span style={styles.tblNum}>{ord.table_number ?? "-"}</span>
        </span>
      </div>

      {ord.waiter_username && (
        <div style={styles.waiterRow}>Cameriere: {ord.waiter_username}</div>
      )}

      {ord.items?.map(it => (
        <div key={it.id} style={styles.itemRow}>
          <span>{it.menu_item_name}&nbsp;×&nbsp;{it.quantity} ({it.status})</span>

          {mode === "todo" && it.status === "pending" && (
            <button
              style={styles.btn(palette.primary)}
              onClick={() => changeItemStatus(it.id, "in_progress")}
            >
              Avvia
            </button>
          )}

          {mode === "todo" && it.status === "in_progress" && (
            <button
              style={styles.btn(palette.success)}
              onClick={() => changeItemStatus(it.id, "ready")}
            >
              Pronto
            </button>
          )}
        </div>
      ))}

      {mode === "ready" && (
        <button
          style={{ ...styles.btn(palette.primary), width: "100%", marginTop: 8 }}
          onClick={() => deliverOrder(ord)}
        >
          Consegna ordine
        </button>
      )}
    </div>
  );

  /* ===============================================================
     UI
  =============================================================== */
  const [tab, setTab] = useState("todo");
  const active   = tab === "todo" ? listTodo : listReady;
  const emptyMsg = tab === "todo"
    ? "Nessun ordine da preparare"
    : "Nessun ordine pronto";

  return (
    <div style={styles.wrapper}>
      {/* tabs */}
      <div style={styles.tabsRow}>
        <div
          style={styles.tabBtn(tab === "todo")}
          onClick={() => setTab("todo")}
        >
          Da&nbsp;preparare&nbsp;({listTodo.length})
        </div>
        <div
          style={styles.tabBtn(tab === "ready")}
          onClick={() => setTab("ready")}
        >
          Pronti&nbsp;({listReady.length})
        </div>
      </div>

      {/* lista */}
      <div style={styles.list}>
        {active.length
          ? active.map(o => renderCard(o, tab))
          : <div style={styles.empty}>{emptyMsg}</div>}
      </div>
    </div>
  );
}
